/**
 * Color Utilities - HSL/RGB conversion and palette generation
 *
 * Generates complete light/dark theme palettes from a single accent color.
 * Uses HSL color space for intuitive color manipulation.
 *
 * @module core/utils/colorUtils
 */

// ============================================================================
// TYPES
// ============================================================================

export interface HSL {
  h: number;  // Hue: 0-360
  s: number;  // Saturation: 0-100
  l: number;  // Lightness: 0-100
}

export interface RGB {
  r: number;  // Red: 0-255
  g: number;  // Green: 0-255
  b: number;  // Blue: 0-255
}

export interface HSV {
  h: number;  // Hue: 0-360
  s: number;  // Saturation: 0-100
  v: number;  // Value/Brightness: 0-100
}

export interface ThemePalette {
  // Core backgrounds
  '--bg-main': string;
  '--bg-card': string;
  '--bg-elevated': string;

  // Text colors
  '--text-main': string;
  '--text-muted': string;
  '--text-subtle': string;

  // Borders
  '--border-color': string;
  '--border-subtle': string;

  // Accent (Primary)
  '--accent-color': string;
  '--accent-color-hover': string;

  // Secondary color (Sub)
  '--secondary-color': string;
  '--secondary-color-hover': string;

  // Finder/Dock specific
  '--finder-bg': string;
  '--finder-item-hover': string;
  '--dock-bg': string;

  // Status colors (accent-based)
  '--status-accent-bg': string;
  '--status-accent-bg-subtle': string;
  '--status-accent-text': string;
  '--status-accent-icon': string;
}

/** Input for theme generation (Main + Sub colors) */
export interface ThemeColors {
  accent: string;        // Primary/Main accent
  secondary?: string;    // Secondary/Sub (optional, derived from accent if not provided)
}

// ============================================================================
// COLOR CONVERSION UTILITIES
// ============================================================================

/**
 * Parse hex color to RGB
 */
export function hexToRgb(hex: string): RGB {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  if (!result || !result[1] || !result[2] || !result[3]) {
    throw new Error(`Invalid hex color: ${hex}`);
  }
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

/**
 * Convert RGB to hex
 */
export function rgbToHex(rgb: RGB): string {
  const toHex = (n: number) => Math.round(Math.max(0, Math.min(255, n)))
    .toString(16)
    .padStart(2, '0');
  return `#${toHex(rgb.r)}${toHex(rgb.g)}${toHex(rgb.b)}`;
}

/**
 * Convert RGB to HSL
 */
export function rgbToHsl(rgb: RGB): HSL {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;

  if (max === min) {
    return { h: 0, s: 0, l: l * 100 };
  }

  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

  let h = 0;
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
      break;
    case g:
      h = ((b - r) / d + 2) / 6;
      break;
    case b:
      h = ((r - g) / d + 4) / 6;
      break;
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    l: Math.round(l * 100),
  };
}

/**
 * Convert HSL to RGB
 */
export function hslToRgb(hsl: HSL): RGB {
  const h = hsl.h / 360;
  const s = hsl.s / 100;
  const l = hsl.l / 100;

  if (s === 0) {
    const gray = Math.round(l * 255);
    return { r: gray, g: gray, b: gray };
  }

  const hue2rgb = (p: number, q: number, t: number) => {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1/6) return p + (q - p) * 6 * t;
    if (t < 1/2) return q;
    if (t < 2/3) return p + (q - p) * (2/3 - t) * 6;
    return p;
  };

  const q = l < 0.5 ? l * (1 + s) : l + s - l * s;
  const p = 2 * l - q;

  return {
    r: Math.round(hue2rgb(p, q, h + 1/3) * 255),
    g: Math.round(hue2rgb(p, q, h) * 255),
    b: Math.round(hue2rgb(p, q, h - 1/3) * 255),
  };
}

/**
 * Convert hex to HSL
 */
export function hexToHsl(hex: string): HSL {
  return rgbToHsl(hexToRgb(hex));
}

/**
 * Convert HSL to hex
 */
export function hslToHex(hsl: HSL): string {
  return rgbToHex(hslToRgb(hsl));
}

// ============================================================================
// HSV (HSB) CONVERSION - For color picker UI
// ============================================================================

/**
 * Convert RGB to HSV
 */
export function rgbToHsv(rgb: RGB): HSV {
  const r = rgb.r / 255;
  const g = rgb.g / 255;
  const b = rgb.b / 255;

  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const d = max - min;

  let h = 0;
  const s = max === 0 ? 0 : d / max;
  const v = max;

  if (max !== min) {
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6;
        break;
      case g:
        h = ((b - r) / d + 2) / 6;
        break;
      case b:
        h = ((r - g) / d + 4) / 6;
        break;
    }
  }

  return {
    h: Math.round(h * 360),
    s: Math.round(s * 100),
    v: Math.round(v * 100),
  };
}

/**
 * Convert HSV to RGB
 */
export function hsvToRgb(hsv: HSV): RGB {
  const h = hsv.h / 360;
  const s = hsv.s / 100;
  const v = hsv.v / 100;

  let r = 0, g = 0, b = 0;

  const i = Math.floor(h * 6);
  const f = h * 6 - i;
  const p = v * (1 - s);
  const q = v * (1 - f * s);
  const t = v * (1 - (1 - f) * s);

  switch (i % 6) {
    case 0: r = v; g = t; b = p; break;
    case 1: r = q; g = v; b = p; break;
    case 2: r = p; g = v; b = t; break;
    case 3: r = p; g = q; b = v; break;
    case 4: r = t; g = p; b = v; break;
    case 5: r = v; g = p; b = q; break;
  }

  return {
    r: Math.round(r * 255),
    g: Math.round(g * 255),
    b: Math.round(b * 255),
  };
}

/**
 * Convert hex to HSV
 */
export function hexToHsv(hex: string): HSV {
  return rgbToHsv(hexToRgb(hex));
}

/**
 * Convert HSV to hex
 */
export function hsvToHex(hsv: HSV): string {
  return rgbToHex(hsvToRgb(hsv));
}

// ============================================================================
// COLOR MANIPULATION
// ============================================================================

/**
 * Adjust HSL values and return hex
 */
export function adjustColor(
  hex: string,
  adjustments: { h?: number; s?: number; l?: number }
): string {
  const hsl = hexToHsl(hex);
  return hslToHex({
    h: (hsl.h + (adjustments.h || 0) + 360) % 360,
    s: Math.max(0, Math.min(100, hsl.s + (adjustments.s || 0))),
    l: Math.max(0, Math.min(100, hsl.l + (adjustments.l || 0))),
  });
}

/**
 * Set specific HSL values
 */
export function setColor(
  hex: string,
  values: { h?: number; s?: number; l?: number }
): string {
  const hsl = hexToHsl(hex);
  return hslToHex({
    h: values.h ?? hsl.h,
    s: values.s ?? hsl.s,
    l: values.l ?? hsl.l,
  });
}

/**
 * Lighten a color
 */
export function lighten(hex: string, amount: number): string {
  return adjustColor(hex, { l: amount });
}

/**
 * Darken a color
 */
export function darken(hex: string, amount: number): string {
  return adjustColor(hex, { l: -amount });
}

/**
 * Desaturate a color
 */
export function desaturate(hex: string, amount: number): string {
  return adjustColor(hex, { s: -amount });
}

/**
 * Mix two colors
 */
export function mixColors(hex1: string, hex2: string, ratio: number = 0.5): string {
  const rgb1 = hexToRgb(hex1);
  const rgb2 = hexToRgb(hex2);
  return rgbToHex({
    r: rgb1.r * (1 - ratio) + rgb2.r * ratio,
    g: rgb1.g * (1 - ratio) + rgb2.g * ratio,
    b: rgb1.b * (1 - ratio) + rgb2.b * ratio,
  });
}

/**
 * Ensure a color has enough lightness contrast against a given background.
 * If the color is too close to the bg lightness, it's pushed away
 * (darkened on light bg, lightened on dark bg) while preserving hue/saturation.
 *
 * Used to keep accent/secondary visible in `text-accent`/`bg-accent` situations
 * even when the user picks a near-white or near-black accent.
 */
export function ensureReadableOn(hex: string, bgLightness: number, minDelta: number = 30): string {
  const hsl = hexToHsl(hex);
  if (Math.abs(hsl.l - bgLightness) >= minDelta) return hex;

  const targetL = bgLightness >= 50
    ? Math.max(0, bgLightness - minDelta - 10)
    : Math.min(100, bgLightness + minDelta + 10);

  return hslToHex({ h: hsl.h, s: hsl.s, l: targetL });
}

// ============================================================================
// PALETTE GENERATION
// ============================================================================

/**
 * Derive a complementary secondary color from the accent
 */
function deriveSecondaryColor(accentHex: string): string {
  return adjustColor(accentHex, { h: 30, s: -10 });
}

/**
 * Generate a complete light mode palette from theme colors
 * @param colors - Theme colors (accent, secondary, etc.)
 * @param contrast - Contrast level 0-100 (default 50)
 *   - 0: Very soft, minimal lightness differences
 *   - 50: Balanced
 *   - 100: High contrast, sharp lightness differences
 */
export function generateLightPalette(colors: ThemeColors | string, contrast: number = 50): ThemePalette {
  // Support legacy single-color input
  const themeColors: ThemeColors = typeof colors === 'string'
    ? { accent: colors }
    : colors;

  const accentHex = themeColors.accent;
  const secondaryHex = themeColors.secondary || deriveSecondaryColor(accentHex);

  const accent = hexToHsl(accentHex);

  // Contrast factor: 0-2 range (0=soft, 1=normal, 2=high)
  const cf = contrast / 50;

  // ===== BACKGROUNDS =====
  // Low contrast: closer to white (98-99)
  // High contrast: slightly darker (94-96)
  const bgMainL = 99 - (cf * 2.5);      // 99 → 94
  const bgCardL = 100 - (cf * 1);       // 100 → 98
  const bgElevatedL = 97 - (cf * 2);    // 97 → 93

  const bgTint = hslToHex({ h: accent.h, s: 5, l: bgMainL });
  const bgCard = hslToHex({ h: accent.h, s: 2, l: Math.min(100, bgCardL) });
  const bgElevated = hslToHex({ h: accent.h, s: 6, l: bgElevatedL });

  // ===== TEXT =====
  // Low contrast: lighter text (less difference from bg)
  // High contrast: darker text (more difference from bg)
  const textMainL = 25 - (cf * 12);     // 25 → 1
  const textMutedL = 50 - (cf * 10);    // 50 → 30
  const textSubtleL = 62 - (cf * 7);    // 62 → 48

  const textMain = hslToHex({ h: accent.h, s: 8, l: Math.max(1, textMainL) });
  const textMuted = hslToHex({ h: accent.h, s: 5, l: textMutedL });
  const textSubtle = hslToHex({ h: accent.h, s: 4, l: textSubtleL });

  // ===== BORDERS =====
  // Low contrast: very subtle borders
  // High contrast: visible borders
  const borderL = 92 - (cf * 12);       // 92 → 68
  const borderSubtleL = 95 - (cf * 10); // 95 → 75

  const borderColor = hslToHex({ h: accent.h, s: 8, l: borderL });
  const borderSubtle = hslToHex({ h: accent.h, s: 5, l: borderSubtleL });

  // Clamp accent/secondary so they stay legible against bg-card (text-accent, bg-accent).
  // Pure white or near-white accents would otherwise vanish on the white card surface.
  const safeAccent = ensureReadableOn(accentHex, bgCardL);
  const safeSecondary = ensureReadableOn(secondaryHex, bgCardL);

  // Color variations (hover states)
  const accentHover = adjustColor(safeAccent, { l: -8 });
  const secondaryHover = adjustColor(safeSecondary, { l: -8 });

  // Finder/Dock
  const finderBg = bgCard;
  const finderItemHover = hslToHex({ h: accent.h, s: 10, l: 94 - (cf * 4) });
  const dockBg = hslToHex({ h: accent.h, s: 5, l: 98 });

  // Status accent colors — text/icon must stay readable on light tint backgrounds
  const statusAccentBg = hslToHex({ h: accent.h, s: accent.s * 0.6, l: 94 });
  const statusAccentBgSubtle = hslToHex({ h: accent.h, s: accent.s * 0.4, l: 97 });
  const statusAccentText = safeAccent;
  const statusAccentIcon = safeAccent;

  return {
    '--bg-main': bgTint,
    '--bg-card': bgCard,
    '--bg-elevated': bgElevated,
    '--text-main': textMain,
    '--text-muted': textMuted,
    '--text-subtle': textSubtle,
    '--border-color': borderColor,
    '--border-subtle': borderSubtle,
    '--accent-color': safeAccent,
    '--accent-color-hover': accentHover,
    '--secondary-color': safeSecondary,
    '--secondary-color-hover': secondaryHover,
    '--finder-bg': finderBg,
    '--finder-item-hover': finderItemHover,
    '--dock-bg': dockBg,
    '--status-accent-bg': statusAccentBg,
    '--status-accent-bg-subtle': statusAccentBgSubtle,
    '--status-accent-text': statusAccentText,
    '--status-accent-icon': statusAccentIcon,
  };
}

/**
 * Generate a complete dark mode palette from theme colors
 * @param colors - Theme colors (accent, secondary, etc.)
 * @param contrast - Contrast level 0-100 (default 50)
 */
export function generateDarkPalette(colors: ThemeColors | string, contrast: number = 50): ThemePalette {
  // Support legacy single-color input
  const themeColors: ThemeColors = typeof colors === 'string'
    ? { accent: colors }
    : colors;

  const accentHex = themeColors.accent;
  const secondaryHex = themeColors.secondary || deriveSecondaryColor(accentHex);

  const accent = hexToHsl(accentHex);

  // Contrast factor: 0-2 range
  const cf = contrast / 50;

  // ===== BACKGROUNDS =====
  // Low contrast: lighter dark (more gray)
  // High contrast: deeper black
  const bgMainL = 8 - (cf * 4);         // 8 → 0
  const bgCardL = 12 - (cf * 4);        // 12 → 4
  const bgElevatedL = 16 - (cf * 4);    // 16 → 8

  const bgMain = hslToHex({ h: accent.h, s: 10, l: Math.max(0, bgMainL) });
  const bgCard = hslToHex({ h: accent.h, s: 8, l: Math.max(2, bgCardL) });
  const bgElevated = hslToHex({ h: accent.h, s: 6, l: Math.max(4, bgElevatedL) });

  // ===== TEXT =====
  // Low contrast: dimmer text
  // High contrast: brighter text
  const textMainL = 85 + (cf * 7.5);    // 85 → 100
  const textMutedL = 60 + (cf * 10);    // 60 → 80
  const textSubtleL = 45 + (cf * 10);   // 45 → 65

  const textMain = hslToHex({ h: accent.h, s: 5, l: Math.min(100, textMainL) });
  const textMuted = hslToHex({ h: accent.h, s: 4, l: textMutedL });
  const textSubtle = hslToHex({ h: accent.h, s: 3, l: textSubtleL });

  // ===== BORDERS =====
  // Low contrast: subtle borders
  // High contrast: visible borders
  const borderL = 15 + (cf * 10);       // 15 → 35
  const borderSubtleL = 12 + (cf * 8);  // 12 → 28

  const borderColor = hslToHex({ h: accent.h, s: 8, l: borderL });
  const borderSubtle = hslToHex({ h: accent.h, s: 6, l: borderSubtleL });

  // Brighter colors for dark mode — 다크 배경에서 항상 보이도록 최소 밝기 보장
  const accentLightness = accent.l;
  const darkAccentL = Math.max(accentLightness + 10, 50);
  const darkAccent = hslToHex({ h: accent.h, s: accent.s, l: Math.min(darkAccentL, 85) });

  const secHsl = hexToHsl(secondaryHex);
  const darkSecL = Math.max(secHsl.l + 10, 50);
  const darkSecondary = hslToHex({ h: secHsl.h, s: secHsl.s, l: Math.min(darkSecL, 85) });

  // Hover states
  const accentHover = adjustColor(darkAccent, { l: 8 });
  const secondaryHover = adjustColor(darkSecondary, { l: 8 });

  // Finder/Dock
  const finderBg = bgCard;
  const finderItemHover = hslToHex({ h: accent.h, s: 12, l: 18 + (cf * 4) });
  const dockBg = hslToHex({ h: accent.h, s: 8, l: Math.max(2, 6 - (cf * 3)) });

  // Status accent colors for dark mode — text/icon은 배경 위 가독성을 위해 더 밝게
  const statusAccentBg = hslToHex({ h: accent.h, s: accent.s * 0.5, l: 20 });
  const statusAccentBgSubtle = hslToHex({ h: accent.h, s: accent.s * 0.3, l: 15 });
  const darkAccentHsl = hexToHsl(darkAccent);
  const statusAccentText = hslToHex({ h: darkAccentHsl.h, s: darkAccentHsl.s, l: Math.max(darkAccentHsl.l, 65) });
  const statusAccentIcon = statusAccentText;

  return {
    '--bg-main': bgMain,
    '--bg-card': bgCard,
    '--bg-elevated': bgElevated,
    '--text-main': textMain,
    '--text-muted': textMuted,
    '--text-subtle': textSubtle,
    '--border-color': borderColor,
    '--border-subtle': borderSubtle,
    '--accent-color': darkAccent,
    '--accent-color-hover': accentHover,
    '--secondary-color': darkSecondary,
    '--secondary-color-hover': secondaryHover,
    '--finder-bg': finderBg,
    '--finder-item-hover': finderItemHover,
    '--dock-bg': dockBg,
    '--status-accent-bg': statusAccentBg,
    '--status-accent-bg-subtle': statusAccentBgSubtle,
    '--status-accent-text': statusAccentText,
    '--status-accent-icon': statusAccentIcon,
  };
}

/**
 * Generate both light and dark palettes
 * @param colors - Theme colors
 * @param contrast - Contrast level 0-100 (default 50)
 */
export function generateThemePalettes(colors: ThemeColors | string, contrast: number = 50): {
  light: ThemePalette;
  dark: ThemePalette;
} {
  return {
    light: generateLightPalette(colors, contrast),
    dark: generateDarkPalette(colors, contrast),
  };
}

// ============================================================================
// PRESET COLORS
// ============================================================================

/**
 * Preset accent colors for quick selection
 */
export const PRESET_COLORS = [
  { hex: '#3b82f6', name: '블루', nameEn: 'Blue' },
  { hex: '#8b5cf6', name: '바이올렛', nameEn: 'Violet' },
  { hex: '#ec4899', name: '핑크', nameEn: 'Pink' },
  { hex: '#ef4444', name: '레드', nameEn: 'Red' },
  { hex: '#f97316', name: '오렌지', nameEn: 'Orange' },
  { hex: '#eab308', name: '옐로우', nameEn: 'Yellow' },
  { hex: '#22c55e', name: '그린', nameEn: 'Green' },
  { hex: '#14b8a6', name: '틸', nameEn: 'Teal' },
  { hex: '#06b6d4', name: '시안', nameEn: 'Cyan' },
  { hex: '#6366f1', name: '인디고', nameEn: 'Indigo' },
  { hex: '#a855f7', name: '퍼플', nameEn: 'Purple' },
  { hex: '#64748b', name: '슬레이트', nameEn: 'Slate' },
] as const;

/**
 * Validate hex color format
 */
export function isValidHex(hex: string): boolean {
  return /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex);
}

/**
 * Normalize hex color (ensure # prefix and 6 digits)
 */
export function normalizeHex(hex: string): string {
  let normalized = hex.trim();
  if (!normalized.startsWith('#')) {
    normalized = '#' + normalized;
  }
  // Expand 3-digit hex to 6-digit
  if (/^#[A-Fa-f0-9]{3}$/.test(normalized)) {
    normalized = '#' + normalized[1] + normalized[1] + normalized[2] + normalized[2] + normalized[3] + normalized[3];
  }
  return normalized.toLowerCase();
}
